"use client";

import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";

// Pricing configuration
const MONTHLY_PRICE = 48.75; // Base monthly price in dollars
const ANNUAL_DISCOUNT_PERCENT = 20; // Discount percentage for annual billing

// Calculate annual price with discount
const calculateAnnualPrice = (
	monthlyPrice: number,
	discountPercent: number,
): number => {
	return monthlyPrice * (1 - discountPercent / 100);
};

// Components defined outside to avoid lint issues
const CheckIcon = ({ className }: { className?: string }) => (
	<Check className={`${className} w-6 h-6 text-[var(--landing-page-accent)]`} />
);

interface BillingToggleProps {
	isAnnual: boolean;
	setIsAnnual: (value: boolean) => void;
	t: (key: string) => string;
}

const BillingToggle = ({ isAnnual, setIsAnnual, t }: BillingToggleProps) => (
	<div className="flex items-center justify-center gap-4 sm:mb-5">
		<span
			className={`text-sm font-medium ${!isAnnual ? "text-foreground" : "text-muted-foreground"}`}
		>
			{t("billing.monthly")}
		</span>
		<button
			type="button"
			onClick={() => setIsAnnual(!isAnnual)}
			className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none
        ${isAnnual ? "bg-[var(--landing-page-accent)]" : "bg-muted"}
      `}
		>
			<span
				className={`
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out
          ${isAnnual ? "translate-x-6" : "translate-x-1"}
        `}
			/>
		</button>
		<span
			className={`text-sm font-medium ${isAnnual ? "text-foreground" : "text-muted-foreground"}`}
		>
			{t("billing.annual")}
		</span>
		{isAnnual && (
			<span className="text-[var(--landing-page-accent-foreground)] text-xs font-semibold px-2 py-1 rounded-full bg-[var(--landing-page-accent)]">
				{t("billing.discount")}
			</span>
		)}
	</div>
);

export function PricingCards() {
	const [isAnnual, setIsAnnual] = useState(true);
	const t = useTranslations("Pricing");
	const posthog = usePostHog();

	const pricingPlans = [
		{
			name: t("plans.explorer.name"),
			subtitle: t("plans.explorer.subtitle"),
			price: "Free",
			period: "",
			description: t("plans.explorer.description"),
			features: [
				t("plans.explorer.features.0"),
				t("plans.explorer.features.1"),
				t("plans.explorer.features.2"),
				t("plans.explorer.features.3"),
				t("plans.explorer.features.4"),
				t("plans.explorer.features.5"),
				t("plans.explorer.features.6"),
				t("plans.explorer.features.7"),
				t("plans.explorer.features.8"),
			],
			buttonText: t("plans.explorer.buttonText"),
			buttonStyle:
				"text-[var(--landing-page-accent-foreground)] font-medium hover:opacity-90 bg-[var(--landing-page-accent)]",
			popular: false,
		},
		{
			name: t("plans.growth.name"),
			subtitle: t("plans.growth.subtitle"),
			price: isAnnual
				? `$${calculateAnnualPrice(MONTHLY_PRICE, ANNUAL_DISCOUNT_PERCENT)}`
				: `$${MONTHLY_PRICE}`,
			period: t("plans.growth.period"),
			description: t("plans.growth.description"),
			features: [
				t("plans.growth.features.0"),
				t("plans.growth.features.1"),
				t("plans.growth.features.2"),
				t("plans.growth.features.3"),
				t("plans.growth.features.4"),
				t("plans.growth.features.5"),
				t("plans.growth.features.6"),
				t("plans.growth.features.7"),
				t("plans.growth.features.9"),
			],
			buttonText: t("plans.growth.buttonText"),
			buttonStyle:
				"text-[var(--landing-page-accent-foreground)] font-medium hover:opacity-90 bg-[var(--landing-page-accent)]",
			popular: true,
		},
		{
			name: t("plans.enterprise.name"),
			subtitle: t("plans.enterprise.subtitle"),
			price: t("plans.enterprise.price"),
			period: "",
			description: t("plans.enterprise.description"),
			features: [
				t("plans.enterprise.features.0"),
				t("plans.enterprise.features.1"),
				t("plans.enterprise.features.2"),
				t("plans.enterprise.features.3"),
				t("plans.enterprise.features.4"),
				t("plans.enterprise.features.5"),
				t("plans.enterprise.features.6"),
				t("plans.enterprise.features.7"),
				t("plans.enterprise.features.8"),
			],
			buttonText: t("plans.enterprise.buttonText"),
			buttonStyle:
				"text-[var(--landing-page-accent-foreground)] font-medium hover:opacity-90 bg-[var(--landing-page-accent)]",
			popular: false,
		},
	];

	return (
		<div className="w-full max-w-7xl mx-auto">
			<BillingToggle isAnnual={isAnnual} setIsAnnual={setIsAnnual} t={t} />

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
				{pricingPlans.map((plan) => (
					<div
						key={plan.name}
						className={`
              relative rounded-2xl border-2 p-8 shadow-lg transition-all duration-200 mt-10
              ${
								plan.popular
									? "border-border bg-muted/20 scale-105 shadow-2xl shadow-black/20 dark:shadow-2xl dark:shadow-[#6EFCD9]/50"
									: "border-border bg-background hover:border-muted-foreground/20"
							}
            `}
					>
						{plan.popular && (
							<div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
								<span className="text-[var(--landing-page-accent-foreground)] px-4 py-1 rounded-full text-sm font-semibold bg-[var(--landing-page-accent)]">
									{t("popular")}
								</span>
							</div>
						)}

						<div className="text-center mb-8">
							<h3 className="text-2xl font-bold text-foreground mb-2">
								{plan.name}
							</h3>
							<p className="text-muted-foreground text-sm mb-4">
								{plan.description}
							</p>
							<div className="flex items-baseline justify-center">
								<span className="text-4xl font-bold text-foreground">
									{plan.price}
								</span>
								<span className="text-muted-foreground ml-1">
									{plan.period}
								</span>
							</div>
							{isAnnual &&
								plan.price !== "$0" &&
								plan.price !== "Free" &&
								plan.price !== t("plans.enterprise.price") && (
									<p className="text-sm mt-2 text-[var(--landing-page-accent)]">
										{t("billing.savings")}
									</p>
								)}
						</div>

						<button
							type="button"
							onClick={() => {
								posthog?.capture("pricing_plan_clicked", {
									plan_name: plan.name,
									plan_price: plan.price,
									billing_type: isAnnual ? "annual" : "monthly",
									is_popular: plan.popular,
								});
								window.open("https://buy.stripe.com/test_5kQ7sNeyi2o0egjgJi83C00", "_blank");
							}}
							className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-colors duration-200 mb-8 ${plan.buttonStyle}`}
						>
							{plan.buttonText}
						</button>

						<ul className="space-y-4">
							{plan.features.map((feature, featureIndex) => (
								<li
									key={`${plan.name}-feature-${featureIndex}`}
									className="flex items-start gap-3"
								>
									<CheckIcon className="flex-shrink-0 mt-0.5" />
									<span className="text-foreground text-md">{feature}</span>
								</li>
							))}
						</ul>
					</div>
				))}
			</div>
		</div>
	);
}
